import React, { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HandNotesTable from '../app/components/HandNotesTable';
import CoachingBubble from '../components/CoachingBubble/CoachingBubble';
import '../app/components/HandNotesTable.css';
import { PlayFab } from '../app/services/PlayFab';
import { AnalysisDisplay } from '../app/components/AnalysisDisplay';
import { Network } from '../app/services/Network';
import { HandHistoryCard } from '../app/components/HandHistoryCard';
import { evaluateTexasHoldemHand } from '../utils/handStrengthEvaluator';

// Reuse types from the existing replayer to avoid redefining shapes
import { HandSummaryFull, HandSummaryStreetAction } from './HandReplayerPage';
import { Street } from './PlaygroundPage/PlaygroundPage';
import { HandAnalysisData } from '../app/types';

type LastActionsMap = Record<number, Array<{ action: string; amount?: number; number: number }>>;
type ChatMsg = { id: string; role: 'user'|'assistant'; text: string; bullets?: string[] };

interface PlayerSeat {
  id: number;
  position: string;
  isActive: boolean;
  stackSize: number;
}

const streetsOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];

const SimpleReplayerPage: React.FC = () => {
  const location = useLocation() as any;
  const params = useParams<{ playfabId?: string; handId?: string }>();
  const navigate = useNavigate();
  const navState = (location?.state || {}) as {
    handSummary?: HandSummaryFull;
    handId?: string;
    ownerPlayfabId?: string;
  };

  const [handData, setHandData] = useState<HandSummaryFull | null>(navState.handSummary || null);
  const [loadedAnalysis, setLoadedAnalysis] = useState<HandAnalysisData | null>(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Table state
  const [players, setPlayers] = useState<PlayerSeat[]>([]);
  const [heroPosition, setHeroPosition] = useState<string | undefined>(undefined);
  const [currentStreet, setCurrentStreet] = useState<Street>('preflop');
  const [currentActionIndex, setCurrentActionIndex] = useState<number>(-1);
  const [lastActions, setLastActions] = useState<LastActionsMap>({});
  const [currentTurnPlayerId, setCurrentTurnPlayerId] = useState<number | null>(null);
  const [potSize, setPotSize] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2000);
  };

  // Coach chat state
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Flatten actions for step-through
  const flatActions = useMemo(() => {
    if (!handData) return [] as Array<{ street: Street; action: HandSummaryStreetAction; indexInStreet: number }>;
    const entries: Array<{ street: Street; action: HandSummaryStreetAction; indexInStreet: number }> = [];
    streetsOrder.forEach(st => {
      const list = (handData as any)[st]?.actions || [];
      list.forEach((a: HandSummaryStreetAction, idx: number) => entries.push({ street: st, action: a, indexInStreet: idx }));
    });
    return entries;
  }, [handData]);

  // Lazy-connect on first send to avoid unhandled rejections if WS is down

  const buildCoachingContext = () => {
    const id = navState.handId || params.handId;
    const ownerId = params.playfabId || navState.ownerPlayfabId || PlayFab.getPlayerId();
    const currentStreetData = (handData as any)?.[currentStreet] || {};
    const minimalAnalysis = loadedAnalysis?.streets?.[currentStreet] || null;

    return {
      handId: id || null,
      ownerPlayfabId: ownerId || null,
      currentStreet,
      potSize,
      heroPosition,
      board: (handData?.board || []).slice(0, currentStreet === 'flop' ? 3 : currentStreet === 'turn' ? 4 : currentStreet === 'river' ? 5 : 0),
      streetAnalysis: minimalAnalysis,
      recentActions: (currentStreetData.actions || []).slice(-8)
    };
  };

  const sendCoachingMessage = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    const userMsg: ChatMsg = { id: crypto.randomUUID(), role: 'user', text };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');

    try {
      await Network.ensureConnection();
      const context = buildCoachingContext();
      const response = await Network.sendEventWithResponse({
        id: 'coaching-advice',
        message: text,
        context
      });
      const bulletsArr = Array.isArray((response || {}).bullets) ? (response as any).bullets as string[] : undefined;
      const assistantText = (bulletsArr && bulletsArr.length)
        ? bulletsArr.map(b => `- ${b}`).join('\n')
        : ((response && (response.text || response.message)) ? (response.text || response.message) : JSON.stringify(response || {}));
      const botMsg: ChatMsg = { id: crypto.randomUUID(), role: 'assistant', text: assistantText, bullets: bulletsArr };
      setChatMessages(prev => [...prev, botMsg]);
    } catch (e) {
      const botMsg: ChatMsg = { id: crypto.randomUUID(), role: 'assistant', text: 'Sorry, I could not get coaching advice right now.' };
      setChatMessages(prev => [...prev, botMsg]);
    } finally {
      setChatSending(false);
    }
  };

  // Helpers to respect folds from earlier streets
  const getStreetIndex = (s: Street) => streetsOrder.indexOf(s);
  const computeFoldedBeforeStreet = (s: Street): Set<number> => {
    const folded = new Set<number>();
    if (!handData) return folded;
    const idx = getStreetIndex(s);
    streetsOrder.slice(0, Math.max(0, idx)).forEach(st => {
      const acts: HandSummaryStreetAction[] = (handData as any)[st]?.actions || [];
      acts.forEach(a => {
        const t = (a.type || '').toString().toLowerCase();
        if (t === 'fold' && typeof a.playerId === 'number') folded.add(a.playerId);
      });
    });
    return folded;
  };
  const buildBaselinePlayersForStreet = (s: Street): PlayerSeat[] => {
    if (!handData) return players; // fallback
    const folded = computeFoldedBeforeStreet(s);
    return handData.players.map(p => ({
      id: p.id,
      position: p.position,
      isActive: !folded.has(p.id),
      stackSize: p.initialStack,
    }));
  };

  // Bootstrap from nav state or fetch by handId
  useEffect(() => {
    let cancelled = false;
    const id = navState.handId || params.handId;
    if (!id) return;
    if (loadedAnalysis) return; // already have analysis
    setIsLoadingAnalysis(true);
    setAnalysisError(null);

    const ownerId = params.playfabId || navState.ownerPlayfabId || PlayFab.getPlayerId();
    let attempts = 0;
    const maxAttempts = 10;

    const fetchOnce = async () => {
      try {
        const id = navState.handId || params.handId;
        const res = await PlayFab.executeCloudScript('getDynamoDBData', {
          action: 'getHand',
          handId: id,
          ownerPlayfabId: ownerId
        });

        // EXACT nested parsing like HandHistoryPage
        const nestedResult = (res as any)?.data?.FunctionResult?.FunctionResult;
        console.log('HandReview: nestedResult from getHand:', nestedResult);

        if (res.success && nestedResult?.success) {
          let handAnalysis = nestedResult.data as HandAnalysisData;
          // Fallback: if structured fields missing but analysisJson exists, hydrate from it
          try {
            const aj = (handAnalysis as any)?.analysisJson;
            const hasStreets = !!(handAnalysis as any)?.streets;
            if (!hasStreets && aj) {
              const parsed = typeof aj === 'string' ? JSON.parse(aj) : aj;
              if (parsed && typeof parsed === 'object') {
                handAnalysis = { ...(handAnalysis as any), ...parsed } as any;
              }
            }
          } catch {}
          if (!cancelled && handAnalysis) {
            // hydrate summary if present and we don't already have one
            const maybeSummary = (handAnalysis as any)?.summaryJson;
            if (!navState.handSummary && maybeSummary) {
              try {
                const summary = typeof maybeSummary === 'string' ? JSON.parse(maybeSummary) : maybeSummary;
                if (summary?.metadata && (summary?.preflop || summary?.board)) setHandData(summary as HandSummaryFull);
              } catch {}
            }
            // Also accept structured summary if provided directly
            if (!navState.handSummary && !handData && (handAnalysis as any)?.summary) {
              const s = (handAnalysis as any).summary;
              if (s && (s.metadata || s.preflop || s.board)) setHandData(s as any);
            }
            setLoadedAnalysis(handAnalysis);
            setIsLoadingAnalysis(false);
            return true;
          }
        } else {
          const errMsg = nestedResult?.error || (res as any)?.data?.FunctionResult?.error || 'Unknown error fetching hand';
          console.warn('HandReview: getHand reported failure:', errMsg, nestedResult?.details);
        }
      } catch (e: any) {
        if (!cancelled) setAnalysisError(e?.message || 'Failed to load analysis');
      }
      return false;
    };

    // initial try
    fetchOnce().then((ok) => {
      if (ok || cancelled) return;
      const interval = setInterval(async () => {
        if (cancelled) { clearInterval(interval); return; }
        attempts++;
        const okNow = await fetchOnce();
        if (okNow || attempts >= maxAttempts) {
          clearInterval(interval);
          if (!okNow && !cancelled) setIsLoadingAnalysis(false);
        }
      }, 3000);
    });
    return () => { cancelled = true; };
  }, [navState.handId, params.handId, loadedAnalysis]);

  // Initialize players and hero position when hand loads
  useEffect(() => {
    if (!handData) return;
    const hero = handData.players.find(p => p.isHero);
    setHeroPosition(hero?.position);
    // Start at first street that has actions
    const firstStreet = streetsOrder.find(s => (handData as any)[s]?.actions?.length);
    const street = firstStreet || 'preflop';
    setCurrentStreet(street);
    setCurrentActionIndex(-1);
    setLastActions({});
    setPlayers(buildBaselinePlayersForStreet(street));
    // Pot at start of the first street
    const ps = (handData as any)[street]?.potSizeAtStart ?? 0;
    setPotSize(ps);
  }, [handData]);

  // Helper: recompute pot and seat badges from 0..currentActionIndex on currentStreet
  const rebuildStateForPointer = (street: Street, actionIdx: number) => {
    if (!handData) return;
    const streetData = (handData as any)[street];
    const basePot = streetData?.potSizeAtStart ?? 0;
    const acts: HandSummaryStreetAction[] = streetData?.actions || [];
    const upto = Math.min(actionIdx + 1, acts.length);
    const newLast: LastActionsMap = {};
    let runningPot = basePot;
    // Always start from baseline for this street so previously folded seats stay folded
    const updatedPlayers = buildBaselinePlayersForStreet(street);

    // prepare blind seat ids and stakes if available for better mapping
    const stakes = handData?.metadata?.stakes;
    const smallBlind = stakes?.smallBlind ?? null;
    const bigBlind = stakes?.bigBlind ?? null;
    const sbSeat = players.find(p => (p.position || '').toLowerCase() === 'sb')?.id;
    const bbSeat = players.find(p => (p.position || '').toLowerCase() === 'bb')?.id;

    for (let i = 0; i < upto; i++) {
      const a = acts[i];
      let seatId = a.playerId;
      // If this is a posts action and we have stakes + blind seats, map accurately
      const t = (a.type || '').toString().toLowerCase();
      if (t === 'posts' && typeof a.amount === 'number') {
        if (smallBlind != null && Math.abs(a.amount - smallBlind) < 1e-6 && sbSeat != null) {
          seatId = sbSeat;
        } else if (bigBlind != null && Math.abs(a.amount - bigBlind) < 1e-6 && bbSeat != null) {
          seatId = bbSeat;
        }
      }
      const entry = { action: a.type.toLowerCase(), amount: a.amount ?? undefined, number: i + 1 };
      newLast[seatId] = (newLast[seatId] || []).concat(entry);
      // Avoid double-counting blinds if they were already included in potSizeAtStart
      const actionTypeLc = (a.type || '').toString().toLowerCase();
      if (a.amount && a.amount > 0 && actionTypeLc !== 'posts') runningPot += a.amount;
      if (entry.action === 'fold') {
        const idx = updatedPlayers.findIndex(p => p.id === seatId);
        if (idx !== -1) updatedPlayers[idx].isActive = false;
      }
    }
    setLastActions(newLast);
    setPlayers(updatedPlayers);
    setPotSize(runningPot);
    const actorId = acts[actionIdx]?.playerId ?? null;
    setCurrentTurnPlayerId(actorId);
  };

  const nextAction = () => {
    if (!handData) return;
    const streetActs: HandSummaryStreetAction[] = (handData as any)[currentStreet]?.actions || [];
    if (currentActionIndex + 1 < streetActs.length) {
      const ni = currentActionIndex + 1;
      setCurrentActionIndex(ni);
      rebuildStateForPointer(currentStreet, ni);
      return;
    }
    // Move to next street
    const nextStreetIdx = streetsOrder.indexOf(currentStreet) + 1;
    if (nextStreetIdx < streetsOrder.length) {
      const ns = streetsOrder[nextStreetIdx];
      setCurrentStreet(ns);
      setCurrentActionIndex(-1);
      // reset per-street state
      setLastActions({});
      setPlayers(buildBaselinePlayersForStreet(ns));
      setPotSize(((handData as any)[ns]?.potSizeAtStart) ?? potSize);
    }
  };

  const prevAction = () => {
    if (!handData) return;
    if (currentActionIndex >= 0) {
      const ni = currentActionIndex - 1;
      setCurrentActionIndex(ni);
      if (ni >= 0) rebuildStateForPointer(currentStreet, ni);
      else {
        // reset street
        setLastActions({});
        setPlayers(buildBaselinePlayersForStreet(currentStreet));
        setPotSize(((handData as any)[currentStreet]?.potSizeAtStart) ?? 0);
        setCurrentTurnPlayerId(null);
      }
      return;
    }
    // Move to previous street end
    const prevIdx = streetsOrder.indexOf(currentStreet) - 1;
    if (prevIdx >= 0) {
      const ps = streetsOrder[prevIdx];
      const acts: HandSummaryStreetAction[] = (handData as any)[ps]?.actions || [];
      setCurrentStreet(ps);
      setPlayers(buildBaselinePlayersForStreet(ps));
      if (acts.length > 0) {
        setCurrentActionIndex(acts.length - 1);
        rebuildStateForPointer(ps, acts.length - 1);
      } else {
        setCurrentActionIndex(-1);
        setLastActions({});
        setPotSize(((handData as any)[ps]?.potSizeAtStart) ?? 0);
        setCurrentTurnPlayerId(null);
      }
    }
  };

  const renderBoard = () => {
    if (!handData) return null;
    const full = handData.board || [];
    if (currentStreet === 'preflop') return null;
    const flop = full.slice(0, 3);
    const turn = full.slice(3, 4);
    const river = full.slice(4, 5);
    const show = currentStreet === 'flop' ? flop : currentStreet === 'turn' ? flop.concat(turn) : flop.concat(turn).concat(river);
    return (
      <div className="board-display">
        {show.map((c, i) => (
          <div key={`${c}-${i}`} className="card">{formatCard(c)}</div>
        ))}
      </div>
    );
  };

  const formatCard = (card: string) => {
    if (!card || card.length !== 2) return null;
    const rank = card[0];
    const suit = card[1];
    const isRed = suit === 'h' || suit === 'd';
    const suitSymbolMap: Record<string, string> = { h: '♥', d: '♦', c: '♣', s: '♠' };
    const suitSymbol = suitSymbolMap[suit] || suit;
    return (
      <span className={`history-card__content ${isRed ? 'history-card__content--red' : 'history-card__content--black'}`}>
        <span className="history-card__rank">{rank.toUpperCase()}</span>
        <span className="history-card__suit">{suitSymbol}</span>
      </span>
    );
  };

  const currentStreetAnalysis = loadedAnalysis?.streets?.[currentStreet];
  // Derive street analysis with equity fallback from callProfitability.heroEquityPctUsed
  const derivedStreetAnalysis = useMemo(() => {
    const sa: any = currentStreetAnalysis || null;
    if (!sa) return null;
    const cp = sa.callProfitability || null;
    const equityMissing = !(typeof sa.heroEquityPct === 'number');
    const equityFromCP = (cp && typeof cp.heroEquityPctUsed === 'number') ? cp.heroEquityPctUsed : null;
    if (equityMissing && equityFromCP != null) {
      return { ...sa, heroEquityPct: equityFromCP } as any;
    }
    return sa;
  }, [currentStreetAnalysis]);

  // --- Hero cards + hand strength per street ---
  const heroCardsArr = useMemo(() => {
    const c1 = (handData as any)?.heroCards?.holeCard1 as string | undefined;
    const c2 = (handData as any)?.heroCards?.holeCard2 as string | undefined;
    const valid = [c1, c2].filter((c): c is string => !!c && c.length === 2);
    return valid.length === 2 ? valid : [];
  }, [handData]);

  const boardForStreet = useMemo(() => {
    if (!handData) return [] as string[];
    const full = (handData.board || []) as string[];
    if (currentStreet === 'preflop') return [];
    if (currentStreet === 'flop') return full.slice(0, 3);
    if (currentStreet === 'turn') return full.slice(0, 4);
    return full.slice(0, 5);
  }, [handData, currentStreet]);

  const heroEval = useMemo(() => {
    if (heroCardsArr.length !== 2) return null;
    try {
      return evaluateTexasHoldemHand(heroCardsArr, boardForStreet);
    } catch {
      return null;
    }
  }, [heroCardsArr, boardForStreet]);

  // Prefer server-provided outs/equity when available; otherwise use local approximation
  const serverStreet = (loadedAnalysis as any)?.streets?.[currentStreet] || null;
  const serverEquityPct: number | null = (serverStreet && typeof serverStreet.heroEquityPct === 'number')
    ? serverStreet.heroEquityPct
    : ((serverStreet && serverStreet.callProfitability && typeof serverStreet.callProfitability.heroEquityPctUsed === 'number')
      ? serverStreet.callProfitability.heroEquityPctUsed
      : null);
  const serverOuts: number | null = (serverStreet && typeof serverStreet.heroOuts === 'number')
    ? serverStreet.heroOuts
    : ((serverStreet && serverStreet.callProfitability && typeof serverStreet.callProfitability.outs === 'number')
      ? serverStreet.callProfitability.outs
      : null);

  return (
    <div className="page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 960, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => navigate('/handnotes')}
              aria-label="Back to Hand Notes"
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid #D4A017',
                background: '#FFCE3E',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            {/* Removed heading */}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                const ownerId = params.playfabId || navState.ownerPlayfabId || PlayFab.getPlayerId();
                const id = navState.handId || params.handId;
                if (!id) return;
                const url = `${window.location.origin}/hand-review/user/${ownerId}/hand/${id}`;
                try { await navigator.clipboard.writeText(url); } catch {}
                showToast('Share link copied to clipboard');
              }}
              aria-label="Share Hand"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #D4A017', background: '#FFCE3E', color: '#ffffff', cursor: 'pointer' }}
            >
              Share
            </button>
            <button
              onClick={prevAction}
              aria-label="Previous Action"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #D4A017', background: '#FFCE3E', color: '#ffffff', cursor: 'pointer' }}
            >
              ←
            </button>
            <button
              onClick={nextAction}
              aria-label="Next Action"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #D4A017', background: '#FFCE3E', color: '#ffffff', cursor: 'pointer' }}
            >
              →
            </button>
          </div>
        </div>

        {/* Table */}
        <HandNotesTable
          players={players}
          onPlayerClick={setCurrentTurnPlayerId}
          currentTurnPlayerId={currentTurnPlayerId}
          lastActions={lastActions}
          heroPosition={heroPosition}
          onCancelAction={undefined}
          board={renderBoard()}
          potSize={potSize}
        />

        {/* Hero cards + strength panel */}
        {heroCardsArr.length === 2 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: '#111827',
            border: '1px solid #374151',
            borderRadius: 10,
            padding: '8px 12px'
          }}>
            <span style={{ color: '#FFCE3E', fontWeight: 800 }}>Hero</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {heroCardsArr.map((c, i) => (
                <HandHistoryCard key={i} rank={c[0].toUpperCase()} suit={c[1].toLowerCase()} />
              ))}
            </div>
            {(heroEval || serverEquityPct != null || serverOuts != null) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: '#e5e7eb' }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Strength:</span>
                <span style={{ fontSize: 13 }}>{heroEval ? heroEval.description : '—'}</span>
                {((serverEquityPct != null) || (heroEval && typeof heroEval.equityPct === 'number')) && (
                  <span style={{ fontSize: 12, background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 6, padding: '2px 6px' }}>
                    Equity ~ {Math.min(100, Math.max(0, Math.round((serverEquityPct != null ? serverEquityPct : (heroEval!.equityPct as number)) )))}%
                  </span>
                )}
                {(((serverOuts != null) ? serverOuts : (heroEval ? heroEval.outs : undefined)) as any) > 0 && (
                  <span style={{ fontSize: 12, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 6, padding: '2px 6px' }}>
                    Outs: {(serverOuts != null ? serverOuts : heroEval!.outs)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Coaching bubble with animated Chippy under the table */}
        {!isChatOpen && (() => {
          const km = (loadedAnalysis as any)?.keyMistakes as Array<{ street: string; description: string }> | undefined;
          const tips = currentStreetAnalysis?.learningTips || [];
                     const bubbleText = tips.slice(0, 3).join(' • ');
           const show = true;
          return (
            <CoachingBubble
              key={`coach-${currentStreet}`}
              text={bubbleText}
              street={currentStreet}
              isVisible={show}
              streetAnalysis={derivedStreetAnalysis as any}
              keyMistakes={km || null}
              isInDrawer={false}
              tabs={[
                { key: 'preflop', label: 'Preflop', disabled: !(handData as any)?.preflop },
                { key: 'flop', label: 'Flop', disabled: !((handData as any)?.flop || (handData as any)?.board?.length >= 3) },
                { key: 'turn', label: 'Turn', disabled: !((handData as any)?.turn || (handData as any)?.board?.length >= 4) },
                { key: 'river', label: 'River', disabled: !((handData as any)?.river || (handData as any)?.board?.length >= 5) },
              ]}
              activeTabKey={currentStreet}
              onTabSelect={(k)=>{
                setCurrentStreet(k as any);
                const acts = (((handData as any)?.[k]?.actions) || []) as HandSummaryStreetAction[];
                if (acts.length > 0) {
                  const lastIdx = acts.length - 1;
                  setCurrentActionIndex(lastIdx);
                  rebuildStateForPointer(k as Street, lastIdx);
                } else {
                  // Reset per-street state when no actions on that street
                  setCurrentActionIndex(-1);
                  setLastActions({});
                  const ps = (handData as any)?.[k]?.potSizeAtStart ?? potSize;
                  setPotSize(ps);
                  setCurrentTurnPlayerId(null);
                }
              }}
              showChatButton={true}
              onChatWithCoach={() => {
                setIsChatOpen(true);
                setTimeout(() => {
                  const el = document.getElementById('coach-chat-input-modal') || document.getElementById('coach-chat-input');
                  if (el) (el as HTMLInputElement).focus();
                }, 50);
              }}
            />
          );
        })()}

        {/* Full analysis display section with tabs (fallback if coaching not yet shown) */}
        {isLoadingAnalysis && (
          <div style={{ marginTop: 6, color: '#666' }}>Loading analysis…</div>
        )}

        {/* Coach Chat Modal */}
        <Dialog.Root open={isChatOpen} onOpenChange={setIsChatOpen}>
          <Dialog.Portal>
            <Dialog.Overlay style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              position: 'fixed', inset: 0,
              zIndex: 2000
            }} />
            <Dialog.Content style={{
              backgroundColor: '#0b0f17', color: '#e5e7eb',
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: 'min(92vw, 900px)', maxHeight: '85vh', overflow: 'hidden',
              borderRadius: 12, border: '1px solid #374151', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              zIndex: 2001
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottom: '1px solid #1f2937' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src="/images/Chippy.svg" alt="Chippy" style={{ width: 36, height: 36, borderRadius: 18 }} />
                  <Dialog.Title style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Chat with Chippy</Dialog.Title>
                </div>
                <Dialog.Close asChild>
                  <button aria-label="Close" style={{ background: 'transparent', border: 0, color: '#9aa4b2', cursor: 'pointer', fontSize: 18 }}>×</button>
                </Dialog.Close>
              </div>
              <div style={{ display: 'grid', gridTemplateRows: '1fr auto', gap: 10, height: '70vh' }}>
                <div style={{ padding: 12, overflowY: 'auto' }}>
                  {chatMessages.length === 0 && (
                    <div style={{ color: '#9aa4b2' }}>Ask a coaching question about this street or hand...</div>
                  )}
                  {chatMessages.map(m => {
                    if (m.role === 'assistant') {
                      return (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                          <img src="/images/Chippy.svg" alt="Chippy" style={{ width: 32, height: 32, borderRadius: 16, flexShrink: 0, background: '#FFCE3E' }} />
                          <div style={{ background: '#1f2937', color: '#e5e7eb', padding: '8px 12px', borderRadius: 10, maxWidth: '80%' }}>
                            {Array.isArray(m.bullets) && m.bullets.length ? (
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {m.bullets.map((b, i) => (
                                  <li key={i} style={{ marginBottom: 4 }}>{b}</li>
                                ))}
                              </ul>
                            ) : (
                              <span>{m.text}</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                        <div style={{
                          background: '#FFCE3E', color: '#111827',
                          padding: '8px 12px', borderRadius: 10, maxWidth: '80%'
                        }}>{m.text}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #1f2937' }}>
                  <input
                    id="coach-chat-input-modal"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') sendCoachingMessage(); }}
                    placeholder="e.g., Should I be c-betting here with AQo?"
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0b0f17', color: '#e5e7eb' }}
                  />
                  <button
                    onClick={sendCoachingMessage}
                    disabled={chatSending || !chatInput.trim()}
                    style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #D4A017', background: '#FFCE3E', color: '#111827', cursor: chatSending ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                  >
                    {chatSending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {analysisError && (
          <div style={{ marginTop: 6, color: '#b00020' }}>Error: {analysisError}</div>
        )}

        {loadedAnalysis && !isLoadingAnalysis && (
          <div style={{ marginTop: 12 }}>
            <AnalysisDisplay
              analysis={loadedAnalysis as any}
              improvementFeedback={''}
              isImprovingAnalysis={false}
              onImprovementFeedbackChange={() => {}}
              onImproveAnalysisRequest={() => {}}
              onActiveTabChange={(tab)=>{
                // Sync the coaching street with the analysis tab if a street tab is selected
                const lc = (tab||'').toLowerCase();
                if (lc==='preflop' || lc==='flop' || lc==='turn' || lc==='river') {
                  setCurrentStreet(lc as any);
                  setCurrentActionIndex(-1);
                }
              }}
            />
          </div>
        )}

        {/* Inline chat removed per request; modal is the single entry point */}
        {toastMessage && (
          <div style={{ position: 'fixed', bottom: 16, right: 16, background: '#111827', color: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 1000 }}>
            {toastMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleReplayerPage;


